/**
 * @description Utility for date management related operations.
 * @typedef {Moment} MomentDate
 */

import moment from 'moment';

// Constants
export const MIN_DAYS_TO_SHOW_APPLIANCE_STATUS_WARNING = 3;

/**
 * Internal functions
 */
const getTodayMomentDate = () => moment(new Date()).startOf('day');

/**
 * @description Checks if the specified date is a valid date
 * @param {*} date
 * @returns {boolean}
 */
export const isValidDate = date =>
  date !== null && date !== undefined && !isNaN(Date.parse(date));

/**
   * @description Checks if the specified date was created using Momentjs library
   * @param {(string|MomentDate)} date
   * @returns {boolean}
   */

export const isMomentDate = date =>
  date !== null &&
  date !== undefined &&
  date.__proto__.constructor.name === 'Moment'; // eslint-disable-line

/**
   * @description Converts any date formatted string to a date
   * @param {string} date
   * @returns {MomentDate}
   */
export const convertToMomentDate = date =>
  isMomentDate(date) ? date : moment(date);

/**
 * @description Get the precise days as an float number between two dates
 * The first argument should be the lowest date, if not, you will get a negative value.
 * If some of the date arguments are invalid you will get a NaN
 * @param {(string|MomentDate)} lowestDate
 * @param {(string|MomentDate)} [majorDate = today]
 * @returns {number} The precise number of days
 */
export const getPreciseDaysBetweenDates = (lowestDate, majorDate) => {
  if (!isValidDate(lowestDate)) {
    return NaN;
  }

  const momentLowestDate = convertToMomentDate(lowestDate);
  const momentMajorDate = majorDate
    ? convertToMomentDate(majorDate)
    : getTodayMomentDate();

  return momentMajorDate.diff(momentLowestDate, 'days', true);
};

/**
 * @description Get the days as an integer number between two dates
 * The first argument should be the lowest date, if not, you will get a negative value.
 * If some of the date arguments are invalid you will get a NaN
 * @param {(string|MomentDate)} lowestDate
 * @param {(string|MomentDate)} [majorDate = today]
 * @returns {number} The precise number of days
 */
export const getDaysBetweenDates = (lowestDate, majorDate) => {
  const preciseDays = getPreciseDaysBetweenDates(lowestDate, majorDate);

  if (isNaN(preciseDays)) {
    return NaN;
  }

  return Math.floor(preciseDays);
};

/**
 * @description Get the relative time or timeago between an specific date and current one.
 * @param {(string|MomentDate)} date
 * @param {boolean} removeSufix
 * @returns {string} The number of seconds/minutes/days/months/years between the target date
 */
export const getFromNowDate = (date, todayDate, removeSufix) => {
  const validDate =
    !todayDate || !isValidDate(todayDate) ? moment(new Date()) : todayDate;

  if (date) {
    moment.relativeTimeRounding(Math.floor);
    const fromNowDate = moment(date)
      .from(validDate, removeSufix);
    // Reset to default again, in order to prevent side effects.
    moment.relativeTimeRounding(moment.relativeTimeRounding());
    return fromNowDate;
  }

  return null;
};

/**
 * @description Get the month formmated with a prefix
 * @param  {(string|MomentDate)} customDate    A custom date for use instead of today
 * @param  {string} [prefix='in'] A custom prefix
 * @return {string}               [description]
 */
export const getMonthNameWithPrefix = (customDate, prefix = 'in') => {
  const formattedMonth =
    customDate && isValidDate(customDate)
      ? moment(customDate).format('MMMM')
      : moment(new Date()).format('MMMM');

  return `${prefix} ${formattedMonth}`;
};

/**
 * @description Get the formatted date and time for a specific date .
 * @param {(string|MomentDate)} date
 * @param {string} dateFormat
 * @returns {string} The formatted date
 */
export const formatDate = (date, dateFormat) => {
  const formattedDate = 
    date && isValidDate(date) 
    ? moment.utc(date).format(dateFormat)
    : 'No date available';
  return formattedDate;
};

export const formatFriendlyDate = (date) => {
  return formatDate(date, 'dddd, MMMM Do YYYY, h:mm:ss a');
};

export default {
  MIN_DAYS_TO_SHOW_APPLIANCE_STATUS_WARNING,
  getDaysBetweenDates,
  isMomentDate,
  isValidDate,
  getFromNowDate,
  getPreciseDaysBetweenDates,
  getMonthNameWithPrefix,
  formatDate,
  formatFriendlyDate
};
